import {
  DEAL_SOURCE_LABELS,
  type DealDetails,
  type DealHistoryItem,
  formatPhone,
  type StageItem,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ChevronLeft, Pencil, Phone, Trash2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { DealEquipment } from '@/components/client-equipment';
import { Comments } from '@/components/comments';
import { ActivitiesPanel } from '@/components/crm-activities';
import { Money, StageBadge, StageBar } from '@/components/crm-bits';
import { ChatPanel } from '@/components/crm-chat';
import { DealFormDialog, DealItemsDialog, LostReasonDialog } from '@/components/crm-forms';
import { Avatar, Button, ErrorBox, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { contactQuery, crmMetaQuery, dealHistoryQuery, dealQuery } from '@/lib/crm';
import { formatDay, formatMoment, formatMoney } from '@/lib/format';

export function DealPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const q = useQuery(dealQuery(id));
  const meta = useQuery(crmMetaQuery);
  const [editing, setEditing] = useState(false);
  const [editingItems, setEditingItems] = useState(false);
  const [lostStage, setLostStage] = useState<StageItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  const move = useMutation({
    mutationFn: (v: { stageId: string; lostReason?: string }) =>
      api(`/crm/deals/${id}/move`, { method: 'POST', body: v }),
    onSuccess: () => {
      setLostStage(null);
      setError(null);
    },
    onError: (err) => setError(err.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ['crm'] }),
  });
  const take = useMutation({
    mutationFn: () => api(`/crm/deals/${id}/take`, { method: 'POST' }),
    onError: (err) => setError(err.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ['crm'] }),
  });

  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;

  return (
    <div className="max-w-[1240px]">
      <Link to="/crm" className="mb-4 inline-flex items-center gap-1 text-[13px] text-steel hover:text-ink">
        <ChevronLeft className="size-4" aria-hidden />
        Все сделки
      </Link>
      <header className="mb-4 flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] tabular-nums text-steel">
            Сделка #{d.number}
            {d.direction && ` · ${d.direction.name}`}
          </p>
          <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.015em]">{d.title}</h1>
          <p className="mt-1 text-[20px] font-semibold tabular-nums">
            <Money amount={d.amount} currency={d.currency} />
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {d.permissions.canTake && (
            <Button onClick={() => take.mutate()} loading={take.isPending}>
              Взять себе
            </Button>
          )}
          {d.permissions.canEdit && (
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Изменить
            </Button>
          )}
        </div>
      </header>

      {editing && <DealFormDialog deal={d} onClose={() => setEditing(false)} />}
      {editingItems && <DealItemsDialog deal={d} onClose={() => setEditingItems(false)} />}
      {lostStage && (
        <LostReasonDialog
          loading={move.isPending}
          onClose={() => setLostStage(null)}
          onSubmit={(lostReason) => move.mutate({ stageId: lostStage.id, lostReason })}
        />
      )}

      {meta.data && (
        <div className="mb-6">
          <StageBar
            stages={meta.data.stages}
            current={d.stage}
            disabled={!d.permissions.canEdit || move.isPending}
            onPick={(s) => (s.semantic === 'lost' ? setLostStage(s) : move.mutate({ stageId: s.id }))}
          />
          {d.stage.semantic === 'lost' && d.lostReason && (
            <p className="mt-2 text-[13.5px] text-danger">Причина отказа: {d.lostReason}</p>
          )}
          {error && (
            <p role="alert" className="mt-2 text-[13.5px] text-danger">
              {error}
            </p>
          )}
        </div>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-6">
          {d.contact && <ContactChat contactId={d.contact.id} />}
          <Items deal={d} onEdit={() => setEditingItems(true)} />
          <DealEquipment deal={d} />
          <ActivitiesPanel dealId={d.id} />
          {d.description && (
            <section className="panel px-5 py-4">
              <h2 className="mb-1.5 font-semibold">Комментарий</h2>
              <p className="max-w-[75ch] whitespace-pre-wrap break-words">{d.description}</p>
            </section>
          )}
          <Comments entityType="deal" entityId={d.id} />
        </div>

        <aside className="flex flex-col gap-6">
          <section className="panel px-5 py-4">
            <dl className="flex flex-col gap-3.5">
              <Row label="Клиент">
                {d.contact ? (
                  <ClientCard contactId={d.contact.id} />
                ) : (
                  <span className="text-steel">контакт не указан</span>
                )}
              </Row>
              {d.company && (
                <Row label="Компания">
                  <Link to="/crm/companies/$id" params={{ id: d.company.id }} className="link">
                    {d.company.name}
                  </Link>
                </Row>
              )}
              <Row label="Ответственный">
                {d.owner ? (
                  <span className="flex items-center gap-2">
                    <Avatar name={d.owner.fullName} userId={d.owner.userId} size={24} />
                    {d.owner.fullName}
                  </span>
                ) : (
                  <span className="font-medium text-[#7a5b00]">не назначен — возьмите сделку себе</span>
                )}
              </Row>
              <Row label="Стадия">
                <StageBadge stage={d.stage} />
                <span className="ml-2 text-[12.5px] text-steel">с {formatMoment(d.stageChangedAt)}</span>
              </Row>
              <Row label="Дата закрытия (план)">
                {d.closeDate ? formatDay(d.closeDate) : <span className="text-steel">не указана</span>}
              </Row>
              <Row label="Источник">{d.source ? DEAL_SOURCE_LABELS[d.source] : '—'}</Row>
              <Row label="Создана">
                {formatMoment(d.createdAt)}
                {d.createdBy && <span className="text-steel"> · {d.createdBy.fullName}</span>}
              </Row>
              {d.closedAt && <Row label="Закрыта">{formatMoment(d.closedAt)}</Row>}
            </dl>
          </section>
          <History dealId={d.id} />
          {d.permissions.canDelete && <DeleteDeal deal={d} />}
        </aside>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[12.5px] text-steel">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

function ClientCard({ contactId }: { contactId: string }) {
  const c = useQuery(contactQuery(contactId));
  if (!c.data) return <span className="text-steel">…</span>;
  return (
    <div>
      <Link to="/crm/contacts/$id" params={{ id: contactId }} className="link font-medium">
        {c.data.fullName}
      </Link>
      {c.data.position && <span className="block text-[13px] text-steel">{c.data.position}</span>}
      {c.data.phones.map((p) => (
        <a
          key={p}
          href={`tel:${p}`}
          className="mt-0.5 flex items-center gap-1.5 text-[13.5px] hover:text-link"
        >
          <Phone className="size-3.5 text-steel" aria-hidden />
          {formatPhone(p)}
        </a>
      ))}
      {c.data.emails.map((e) => (
        <a key={e} href={`mailto:${e}`} className="block text-[13.5px] text-link hover:underline">
          {e}
        </a>
      ))}
    </div>
  );
}

/** Переписка с клиентом прямо в сделке — если он писал боту. */
function ContactChat({ contactId }: { contactId: string }) {
  const c = useQuery(contactQuery(contactId));
  const [active, setActive] = useState(0);
  const chats = c.data?.chats ?? [];
  if (!chats.length) return null;
  const chat = chats[Math.min(active, chats.length - 1)];
  if (!chat) return null;
  return (
    <div>
      {chats.length > 1 && (
        <div className="mb-2 flex gap-1.5">
          {chats.map((ch, i) => (
            <button
              key={ch.id}
              type="button"
              onClick={() => setActive(i)}
              aria-pressed={i === active}
              className={
                i === active
                  ? 'h-7 rounded bg-graphite px-2.5 text-[13px] text-white'
                  : 'h-7 rounded border border-line-strong px-2.5 text-[13px]'
              }
            >
              {ch.channel === 'telegram' ? 'Telegram' : 'MAX'}
              {ch.unread > 0 && ` · ${ch.unread}`}
            </button>
          ))}
        </div>
      )}
      <ChatPanel chat={chat} className="h-[460px]" />
    </div>
  );
}

function Items({ deal, onEdit }: { deal: DealDetails; onEdit: () => void }) {
  return (
    <section className="panel">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <h2 className="font-semibold">Товары и услуги</h2>
        {deal.permissions.canEdit && (
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="size-3.5" aria-hidden />
            {deal.items.length ? 'Изменить' : 'Добавить'}
          </Button>
        )}
      </div>
      {deal.items.length === 0 ? (
        <p className="px-4 py-3 text-[13.5px] text-steel">
          Позиции не указаны — сумма сделки задана вручную.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13.5px]">
            <tbody className="divide-y divide-line">
              {deal.items.map((i) => (
                <tr key={i.id}>
                  <td className="px-4 py-2">
                    {i.name}
                    {i.sku && <span className="block text-[12px] text-steel">{i.sku}</span>}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-ink-soft">
                    {i.qty} {i.unit} × {formatMoney(i.price, 'RUB')}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right font-medium tabular-nums">
                    {formatMoney(i.total, 'RUB')}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-line-strong">
                <td colSpan={2} className="px-4 py-2 text-right text-steel">
                  Итого
                </td>
                <td className="px-4 py-2 text-right font-semibold tabular-nums">
                  {formatMoney(deal.amount, deal.currency)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}

const FIELD_LABELS: Record<string, string> = {
  title: 'Название',
  stage: 'Стадия',
  amount: 'Сумма',
  ownerId: 'Ответственный',
  contactId: 'Контакт',
  companyId: 'Компания',
  directionId: 'Направление',
  closeDate: 'Дата закрытия',
  description: 'Комментарий',
  source: 'Источник',
  lostReason: 'Причина отказа',
  items: 'Позиций',
  activity: 'Дело',
  result: 'Итог',
};

const ACTION_LABELS: Record<string, string> = {
  created: 'создал сделку',
  updated: 'изменил',
  stage: 'сменил стадию',
  taken: 'взял сделку себе',
  items: 'изменил товары',
  activity_created: 'запланировал',
  activity_done: 'выполнил',
  deleted: 'удалил',
};

function describe(h: DealHistoryItem) {
  const diff = h.diff ?? {};
  if (h.action === 'stage')
    return `${diff.stage?.from} → ${diff.stage?.to}${diff.lostReason ? ` (${diff.lostReason.to})` : ''}`;
  if (h.action === 'activity_created' || h.action === 'activity_done')
    return `${diff.activity?.to ?? ''}${diff.result ? ` — ${diff.result.to}` : ''}`;
  if (h.action === 'updated')
    return Object.keys(diff)
      .map((k) => FIELD_LABELS[k] ?? k)
      .join(', ');
  return '';
}

function History({ dealId }: { dealId: string }) {
  const q = useQuery(dealHistoryQuery(dealId));
  const [all, setAll] = useState(false);
  if (!q.data?.length) return null;
  const items = all ? q.data : q.data.slice(0, 8);
  return (
    <section className="panel px-5 py-4">
      <h2 className="mb-2 font-semibold">История</h2>
      <ol className="flex flex-col gap-2.5 text-[13px]">
        {items.map((h) => (
          <li key={h.id}>
            <span className="text-steel">{formatMoment(h.at)}</span>
            <span className="block">
              <span className="font-medium">{h.actor?.fullName ?? 'Система'}</span>{' '}
              {ACTION_LABELS[h.action] ?? h.action} <span className="text-ink-soft">{describe(h)}</span>
            </span>
          </li>
        ))}
      </ol>
      {q.data.length > 8 && (
        <button type="button" className="link mt-2 text-[13px]" onClick={() => setAll(!all)}>
          {all ? 'Свернуть' : `Вся история (${q.data.length})`}
        </button>
      )}
    </section>
  );
}

function DeleteDeal({ deal }: { deal: DealDetails }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const del = useMutation({
    mutationFn: () => api(`/crm/deals/${deal.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['crm'] });
      await navigate({ to: '/crm' });
    },
  });
  return confirm ? (
    <div className="panel flex flex-col gap-2 px-5 py-4">
      <p className="text-[13.5px]">Удалить сделку #{deal.number}? Клиент и переписка останутся.</p>
      <div className="flex gap-2">
        <Button variant="danger" size="sm" loading={del.isPending} onClick={() => del.mutate()}>
          Удалить
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setConfirm(false)}>
          Отмена
        </Button>
      </div>
    </div>
  ) : (
    <button
      type="button"
      onClick={() => setConfirm(true)}
      className="inline-flex items-center gap-1.5 self-start text-[13px] text-steel hover:text-danger"
    >
      <Trash2 className="size-3.5" aria-hidden />
      Удалить сделку
    </button>
  );
}
